import React, { useEffect, useState, useCallback } from "react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import {
  ShieldAlert,
  Clock,
  GavelIcon,
  CheckCircle2,
  X as XIcon,
  Ban,
  AlertTriangle,
  Info,
  History,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

const RESOLUTIONS = [
  "POLICY_VIOLATION",
  "HARASSMENT_OR_ABUSE",
  "SPAM",
  "PRIVACY",
  "ACADEMIC_INTEGRITY",
  "OTHER",
];

const STATUS_LABEL = {
  OPEN: "Open",
  UNDER_REVIEW: "Under Review",
  ACTION_TAKEN: "Action Taken",
  NO_ACTION: "No Action",
  APPEALED: "Appealed",
  CLOSED: "Closed",
  ESCALATED: "Escalated",
};
const AGE_LABEL = { "<24h": "< 24h", "1-3d": "1–3 days", "3-7d": "3–7 days", "7d+": "7+ days" };

const RESOLVED_STATUSES = new Set(["ACTION_TAKEN", "NO_ACTION", "CLOSED"]);

function Counter({ label, value, icon: Icon, tone }) {
  const tones = {
    open: "bg-amber-50 text-amber-800 border-amber-200",
    review: "bg-blue-50 text-blue-800 border-blue-200",
    resolved: "bg-emerald-50 text-emerald-800 border-emerald-200",
    appealed: "bg-purple-50 text-purple-800 border-purple-200",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone] || ""}`} data-testid={`ts-counter-${tone}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-80">
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
      </div>
      <div className="text-3xl font-black tracking-tight mt-1">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const tone =
    status === "OPEN"
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : status === "UNDER_REVIEW"
      ? "bg-blue-50 text-blue-800 border-blue-200"
      : status === "ACTION_TAKEN"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : status === "NO_ACTION"
      ? "bg-zinc-50 text-zinc-700 border-zinc-200"
      : status === "APPEALED"
      ? "bg-purple-50 text-purple-800 border-purple-200"
      : status === "ESCALATED"
      ? "bg-orange-50 text-orange-800 border-orange-200"
      : "bg-zinc-50 text-zinc-700 border-zinc-200";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-medium ${tone}`}
      data-testid="ts-drawer-status"
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

/**
 * CaseDrawer — right-side drawer displayed only when an authorized moderator
 * clicks a queue row. Fetches details lazily from
 * `GET /api/moderation/circle-reports/{rid}` — the same authorization gate
 * the Phase 9 backend enforces. Never surfaces reporter identity. Displays
 * an escalation notice for conflict-of-interest cases and disables actions.
 * Resolved cases show final outcome and hide action controls.
 */
function CaseDrawer({ caseId, onClose, onResolved }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resolution, setResolution] = useState("POLICY_VIOLATION");
  const [note, setNote] = useState("");
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/moderation/circle-reports/${caseId}`);
      setDetail(r.data);
    } catch (e) {
      const code = e.response?.status;
      if (code === 404) setError("This case is not available to you.");
      else setError("Could not load case.");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  const fetchHistory = useCallback(async () => {
    if (!caseId) return;
    setHistoryLoading(true);
    try {
      const r = await api.get(`/moderation/circle-reports/${caseId}/history`);
      setHistory(r.data);
    } catch (_) {
      // Non-fatal — the primary case content is still usable.
      setHistory(null);
    } finally {
      setHistoryLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    setDetail(null);
    setError(null);
    setHistory(null);
    setResolution("POLICY_VIOLATION");
    setNote("");
    fetchDetail();
    fetchHistory();
  }, [fetchDetail, fetchHistory]);

  const report = detail?.report;
  const conflict = !!detail?.conflict_of_interest;
  const evidence = detail?.evidence;
  const status = report?.status;
  const resolved = status && RESOLVED_STATUSES.has(status);
  const canAct = !!report && !conflict && !resolved;

  const act = useCallback(
    async (action) => {
      if (!report) return;
      if (!RESOLUTIONS.includes(resolution)) {
        toast.error("Choose a resolution");
        return;
      }
      setBusy(true);
      try {
        const r = await api.post(
          `/moderation/circle-reports/${report.id}/action`,
          { action, resolution, note: note.slice(0, 500) },
        );
        toast.success(
          action === "SOFT_REMOVE"
            ? "Message removed. Author notified."
            : "Marked as no action. Case closed.",
        );
        // Refresh authoritative case state so the drawer reflects the new
        // status. Keep the drawer open per spec so the moderator can verify.
        await fetchDetail();
        await fetchHistory();
        // Trigger dashboard counter + queue refresh.
        onResolved?.(r.data?.status || null);
      } catch (e) {
        const code = e.response?.status;
        const msg = e.response?.data?.detail || "Could not complete action";
        if (code === 403) toast.error(msg);
        else if (code === 400) toast.error(msg);
        else if (code === 404) toast.error("Case is no longer available");
        else toast.error("Could not complete action");
      } finally {
        setBusy(false);
      }
    },
    [report, resolution, note, fetchDetail, fetchHistory, onResolved],
  );

  // Reporter identity must never be shown. We never render `reporter_id`,
  // `reporter_note`, or `assigned_to` in the drawer; parent context omits
  // sender identifiers as well.
  const parent = evidence?.parent_context;

  return (
    <div
      className="fixed inset-0 z-40"
      data-testid="ts-case-drawer"
      role="dialog"
      aria-modal="true"
    >
      {/* backdrop */}
      <button
        type="button"
        aria-label="Close case drawer"
        onClick={onClose}
        className="absolute inset-0 bg-black/30"
        data-testid="ts-drawer-backdrop"
      />
      {/* panel */}
      <aside
        className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-white border-l border-border shadow-2xl flex flex-col"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-primary">
              Authorized Case
            </p>
            <h2 className="font-serif text-lg font-black tracking-tight">
              {report ? `Case ${report.id.slice(0, 8)}…` : "Case"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 hover:bg-secondary/60"
            data-testid="ts-drawer-close"
            aria-label="Close"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {loading && (
            <p className="text-sm text-muted-foreground" data-testid="ts-drawer-loading">
              Loading case…
            </p>
          )}
          {!loading && error && (
            <div
              className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
              data-testid="ts-drawer-error"
            >
              {error}
            </div>
          )}

          {!loading && !error && report && (
            <>
              {/* Meta row */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Circle
                  </p>
                  <p className="font-medium" data-testid="ts-drawer-circle">
                    {report.circle_id ? report.circle_id.slice(0, 12) + "…" : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Status
                  </p>
                  <StatusPill status={status} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Reason
                  </p>
                  <p className="font-medium" data-testid="ts-drawer-reason">
                    {report.reason_code || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Opened
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {report.created_at ? new Date(report.created_at).toLocaleString() : "—"}
                  </p>
                </div>
              </div>

              {/* Conflict banner */}
              {conflict && (
                <div
                  className="rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900 flex gap-3"
                  data-testid="ts-drawer-conflict"
                >
                  <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Conflict of interest</p>
                    <p className="mt-1">
                      You are the author of the reported message. This case is escalated for
                      independent review by a platform admin. You cannot act on it, and case
                      evidence is withheld from this drawer to preserve reviewer independence.
                    </p>
                  </div>
                </div>
              )}

              {/* Resolved banner */}
              {resolved && !conflict && (
                <div
                  className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 flex gap-3"
                  data-testid="ts-drawer-resolved"
                >
                  <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Case resolved</p>
                    <p className="mt-1">
                      Outcome:{" "}
                      <span className="font-mono">{STATUS_LABEL[status] || status}</span>
                      {report.resolution && (
                        <>
                          {" "}· Resolution:{" "}
                          <span className="font-mono">{report.resolution}</span>
                        </>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Evidence — hidden for conflict cases (defense-in-depth) */}
              {!conflict && (
                <section className="rounded-xl border border-border" data-testid="ts-drawer-evidence">
                  <header className="px-4 py-2 border-b border-border bg-secondary/40">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      Reported message · body at time of report
                    </p>
                  </header>
                  <div className="p-4 text-sm whitespace-pre-wrap">
                    {evidence?.body_at_report ? (
                      <span data-testid="ts-drawer-body-at-report">
                        {evidence.body_at_report}
                      </span>
                    ) : (
                      <span className="text-muted-foreground italic">
                        No message body captured.
                      </span>
                    )}
                  </div>
                  {parent && (
                    <div className="border-t border-border p-4 text-sm bg-secondary/30">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                        Parent message context
                      </p>
                      <div className="whitespace-pre-wrap text-muted-foreground">
                        {parent.body_at_report || (
                          <span className="italic">No parent body captured.</span>
                        )}
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* Action controls */}
              {canAct && (
                <section
                  className="rounded-xl border border-border p-4 space-y-3"
                  data-testid="ts-drawer-actions"
                >
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Resolution
                  </p>
                  <select
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    className="w-full rounded-md border border-border px-3 py-2 text-sm"
                    data-testid="ts-drawer-resolution"
                    disabled={busy}
                  >
                    {RESOLUTIONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  <Textarea
                    placeholder="Optional moderator note (private, max 500 chars)"
                    maxLength={500}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    data-testid="ts-drawer-note"
                    disabled={busy}
                  />
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      variant="outline"
                      onClick={() => act("NO_ACTION")}
                      disabled={busy}
                      data-testid="ts-drawer-no-action"
                    >
                      <Info className="h-4 w-4 mr-1.5" /> No Action
                    </Button>
                    <Button
                      onClick={() => act("SOFT_REMOVE")}
                      disabled={busy}
                      data-testid="ts-drawer-soft-remove"
                    >
                      <Ban className="h-4 w-4 mr-1.5" /> Soft Remove
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Reporter identity is never shown. Every access is audit-logged.
                  </p>
                </section>
              )}

              {/* Case History — read-only chronological timeline. */}
              <section
                className="rounded-xl border border-border"
                data-testid="ts-drawer-history"
              >
                <header className="px-4 py-2 border-b border-border bg-secondary/40 flex items-center gap-2">
                  <History className="h-3.5 w-3.5 text-muted-foreground" />
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Case history
                  </p>
                </header>
                <div className="max-h-64 overflow-y-auto">
                  {historyLoading && (
                    <p
                      className="p-4 text-xs text-muted-foreground"
                      data-testid="ts-history-loading"
                    >
                      Loading history…
                    </p>
                  )}
                  {!historyLoading && history?.conflict_of_interest && (
                    <p
                      className="p-4 text-xs text-orange-800"
                      data-testid="ts-history-conflict"
                    >
                      History withheld — this case is under independent review.
                    </p>
                  )}
                  {!historyLoading &&
                    history &&
                    !history.conflict_of_interest &&
                    (history.events || []).length === 0 && (
                      <p
                        className="p-4 text-xs text-muted-foreground"
                        data-testid="ts-history-empty"
                      >
                        No history yet.
                      </p>
                    )}
                  {!historyLoading &&
                    history &&
                    !history.conflict_of_interest &&
                    (history.events || []).length > 0 && (
                      <ol className="divide-y divide-border">
                        {history.events.map((ev) => (
                          <li
                            key={ev.id}
                            className="px-4 py-3 flex items-start gap-3"
                            data-testid={`ts-history-event-${ev.kind}`}
                          >
                            <div className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">
                                {ev.label}
                              </p>
                              <p className="text-[11px] text-muted-foreground mt-0.5">
                                {ev.actor_role}
                                {ev.resolution && (
                                  <>
                                    {" · "}
                                    <span className="font-mono">
                                      {ev.resolution}
                                    </span>
                                  </>
                                )}
                                {ev.at && (
                                  <>
                                    {" · "}
                                    {new Date(ev.at).toLocaleString()}
                                  </>
                                )}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ol>
                    )}
                </div>
              </section>
            </>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-border flex justify-between items-center">
          <Button
            variant="outline"
            size="sm"
            disabled={!caseId || loading || error}
            data-testid="ts-drawer-export-btn"
            onClick={async () => {
              if (!caseId) return;
              if (!window.confirm(
                "Export this case for compliance? This action is audited."
              )) return;
              try {
                const r = await api.get(`/moderation/cases/${caseId}/export`);
                const blob = new Blob([JSON.stringify(r.data, null, 2)],
                                       { type: "application/json" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = r.data.filename ||
                             `doccircle-case-${caseId}.json`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success(`Case exported (sha256: ${r.data.sha256?.slice(0, 12)}…)`);
              } catch (e) {
                toast.error(
                  e.response?.status === 404
                    ? "This case is not available to you."
                    : "Could not export case."
                );
              }
            }}
          >
            Export Case
          </Button>
          <Button variant="ghost" onClick={onClose} data-testid="ts-drawer-close-footer">
            Close
          </Button>
        </footer>
      </aside>
    </div>
  );
}

export default function TrustSafetyDashboard() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("OPEN");
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/moderation/dashboard", { params: { status } });
      setData(r.data);
      setForbidden(false);
    } catch (e) {
      if (e.response?.status === 403) {
        setForbidden(true);
        setData(null);
      } else toast.error("Could not load moderation dashboard");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  // Live counter updates on any moderation.case.updated in a Circle
  // the caller moderates. We simply re-fetch — persistence is
  // authoritative so reconnects/multi-tab can never inflate counts.
  useEffect(() => {
    let ws;
    try {
      ws = new WebSocket(API.replace(/^http/, "ws") + "/ws/messaging");
      ws.onmessage = (ev) => {
        let d;
        try {
          d = JSON.parse(ev.data);
        } catch (_) {
          return;
        }
        if (d?.type === "moderation.case.updated") load();
      };
    } catch (_) {}
    return () => {
      try {
        ws && ws.close();
      } catch (_) {}
    };
  }, [load]);

  const openCase = useCallback((row) => {
    // Conflict/escalated rows still open — the drawer will render the
    // conflict banner and withhold evidence + actions.
    setSelectedCaseId(row.id);
  }, []);

  const closeDrawer = useCallback(() => {
    setSelectedCaseId(null);
    // Preserve dashboard filter/pagination position — we only clear the
    // drawer selection; status filter state is intact.
  }, []);

  const onCaseResolved = useCallback(() => {
    // Refresh dashboard counters and queue authoritatively.
    load();
  }, [load]);

  if (forbidden) {
    return (
      <div className="max-w-3xl mx-auto p-8" data-testid="ts-forbidden">
        <p className="text-xs uppercase tracking-[0.25em] text-primary">Trust & Safety</p>
        <h1 className="font-serif text-4xl font-black tracking-tight mt-1">Moderation dashboard</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          You are not currently a Circle owner or moderator. Only authorized moderators can view this queue.
        </p>
      </div>
    );
  }

  const c = data?.counters || {};
  return (
    <div className="max-w-6xl mx-auto p-8" data-testid="ts-dashboard">
      <p className="text-xs uppercase tracking-[0.25em] text-primary">Trust & Safety</p>
      <h1 className="font-serif text-4xl font-black tracking-tight mt-1">Moderation dashboard</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Operational queue across every Circle you moderate. Reported message content stays hidden until you open an authorized case.
      </p>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Counter label="Open" value={c.open ?? 0} icon={ShieldAlert} tone="open" />
        <Counter label="Under Review" value={c.under_review ?? 0} icon={Clock} tone="review" />
        <Counter label="Resolved" value={c.resolved ?? 0} icon={CheckCircle2} tone="resolved" />
        <Counter label="Appealed" value={c.appealed ?? 0} icon={GavelIcon} tone="appealed" />
      </div>
      {c.oldest_open_age_bucket && (
        <p className="mt-3 text-[11px] uppercase tracking-widest text-amber-700" data-testid="ts-oldest-open">
          Oldest open case: {AGE_LABEL[c.oldest_open_age_bucket] || c.oldest_open_age_bucket}
        </p>
      )}

      <div className="mt-8 flex items-center gap-2" data-testid="ts-filters">
        {["OPEN", "UNDER_REVIEW", "RESOLVED", "APPEALED"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`ts-filter-${s}`}
            className={`text-[11px] uppercase tracking-widest rounded-full border px-3 py-1 ${
              status === s
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border text-muted-foreground hover:bg-secondary/60"
            }`}
          >
            {STATUS_LABEL[s] || s}
          </button>
        ))}
      </div>

      <div className="mt-4 rounded-2xl border border-border overflow-hidden" data-testid="ts-queue">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-2">Case</th>
              <th className="text-left px-4 py-2">Circle</th>
              <th className="text-left px-4 py-2">Reason</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Age</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && (data?.queue || []).length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-6 text-center text-muted-foreground"
                  data-testid="ts-queue-empty"
                >
                  No cases in this bucket. All caught up.
                </td>
              </tr>
            )}
            {!loading &&
              (data?.queue || []).map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-border cursor-pointer hover:bg-secondary/40"
                  data-testid={`ts-row-${row.id}`}
                  onClick={() => openCase(row)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openCase(row);
                    }
                  }}
                >
                  <td className="px-4 py-2 font-mono text-[11px]">{row.id.slice(0, 8)}…</td>
                  <td className="px-4 py-2">{row.circle_name || "—"}</td>
                  <td className="px-4 py-2">{row.reason_code || "—"}</td>
                  <td className="px-4 py-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-medium">
                      {STATUS_LABEL[row.status] || row.status}
                    </span>
                    {row.conflict_of_interest && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-700">
                        Conflict
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-[11px] text-muted-foreground">
                    {AGE_LABEL[row.age_bucket] || row.age_bucket}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {selectedCaseId && (
        <CaseDrawer
          caseId={selectedCaseId}
          onClose={closeDrawer}
          onResolved={onCaseResolved}
        />
      )}
    </div>
  );
}
